import type { PlayerPublic, RoomView } from "@shared/types";
import { SPY_MAX_ANSWER } from "@shared/games/spy";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { Avatar } from "../../components/Avatar";
import { Btn } from "../../components/Button";
import { Check, Eye, EyeOff, Send } from "../../components/Icon";
import { TimerBar } from "../../components/Timer";
import { useI18n } from "../../i18n";
import type { RoomClient } from "../../lib/room-client";
import { avatarVoice, haptic, play } from "../../lib/sfx";
import { AudienceNote, Banner, GameChrome, PlayerStrip, Scoreboard } from "../common/GameKit";
import { useAct, useGameAct } from "../common/useRoom";

export interface SpyV {
  phase: "roles" | "question" | "answers" | "vote" | "reveal" | "guess" | "result";
  deadline: number | null;
  phaseMs: number;
  roundNo: number;
  players: string[];
  order: string[];
  seen: string[];
  role: "spy" | "civ" | null;
  word: string | null;
  packName: string | null;
  packEmoji: string;
  spyCount: number;
  question: string | null;
  qi: number;
  qTotal: number;
  speaker: string | null;
  speakerIndex: number;
  answered: string[];
  myAnswer: string | null;
  answers: { q: string; a: Record<string, string> }[];
  voted: string[];
  myVote: string | null;
  votes: Record<string, string> | null;
  accused: string[];
  spies: string[];
  caught: string[];
  guessOptions: string[] | null;
  guesses: Record<string, string> | null;
  winner: "civ" | "spy" | null;
  scores: Record<string, number>;
  roundPts: Record<string, number>;
}

const pl = (view: RoomView, id: string | null) => view.players.find((p) => p.id === id) ?? null;

export function SpyView({ client, view, tv }: { client: RoomClient; view: RoomView; tv?: boolean }) {
  const v = view.view as SpyV;
  const g = useGameAct(client);
  const isHost = !!view.me?.isHost;
  const local = client.local;
  const skip =
    isHost && (v.phase === "question" || v.phase === "answers") ? () => g({ t: "skipToVote" }) : undefined;
  return (
    <>
      <GameChrome client={client} view={view} onSkip={skip} skipLabel={undefined} />
      <div className="shell space-y-6 pt-2">
        {v.phase === "roles" && (local ? <LocalRoles client={client} view={view} v={v} /> : <Roles client={client} view={view} v={v} tv={tv} />)}
        {v.phase === "question" && <Question client={client} view={view} v={v} tv={tv} />}
        {v.phase === "answers" && <Answers client={client} view={view} v={v} />}
        {v.phase === "vote" && (local ? <LocalVote client={client} view={view} v={v} /> : <Vote client={client} view={view} v={v} tv={tv} />)}
        {v.phase === "reveal" && <Reveal client={client} view={view} v={v} />}
        {v.phase === "guess" && <Guess client={client} view={view} v={v} />}
        {v.phase === "result" && <Result client={client} view={view} v={v} />}
      </div>
    </>
  );
}

/** Press and hold to peek; the card hides again as soon as you let go. */
function RoleCard({ role, word, pack, emoji, spyCount, onSeen, seen }: { role: SpyV["role"]; word: string | null; pack: string | null; emoji: string; spyCount: number; onSeen?(): void; seen?: boolean }) {
  const { d, f } = useI18n();
  const [peek, setPeek] = useState(false);
  const spy = role === "spy";
  return (
    <div className="space-y-4">
      <button
        className="relative block aspect-[4/5] w-full max-w-sm select-none overflow-hidden rounded-[32px] mx-auto"
        style={{ background: peek ? (spy ? "var(--color-candy)" : "var(--color-spy)") : "var(--color-ground-3)", touchAction: "none" }}
        onPointerDown={(e) => {
          (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
          setPeek(true);
          haptic(15);
          play(spy ? "sting" : "pop", { volume: 0.5 });
        }}
        onPointerUp={() => setPeek(false)}
        onPointerCancel={() => setPeek(false)}
        onContextMenu={(e) => e.preventDefault()}
        aria-label={d.spy.tapToSee}
      >
        <AnimatePresence mode="wait">
          {peek ? (
            <motion.div key="in" initial={{ rotateY: 90 }} animate={{ rotateY: 0 }} exit={{ rotateY: -90 }} transition={{ duration: 0.18 }} className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-6 text-center" style={{ color: spy ? "#fff" : "#132100" }}>
              {spy ? (
                <>
                  <Avatar index={15} size={110} disc={false} mood="talk" />
                  <p className="display text-4xl">{d.spy.youAreSpy}</p>
                  <p className="text-lg font-bold opacity-90">{pack ? f(d.spy.spyTheme, { pack: `${emoji} ${pack}` }) : d.spy.spyNoTheme}</p>
                  <p className="text-sm font-semibold opacity-80">{d.spy.spyHint}</p>
                </>
              ) : (
                <>
                  <p className="font-bold opacity-80">{d.spy.secretWord}</p>
                  <p className="display text-[clamp(34px,9vw,54px)] leading-tight">{word}</p>
                  <p className="text-lg font-bold opacity-80">{emoji} {pack}</p>
                  <p className="text-sm font-semibold opacity-80">{d.spy.civHint}</p>
                </>
              )}
            </motion.div>
          ) : (
            <motion.div key="out" initial={{ rotateY: -90 }} animate={{ rotateY: 0 }} exit={{ rotateY: 90 }} transition={{ duration: 0.18 }} className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-6 text-center">
              <Eye size={54} className="text-spy" />
              <p className="display text-3xl">{d.spy.tapToSee}</p>
              <p className="font-semibold text-ink-3">{d.spy.hideFromOthers}</p>
            </motion.div>
          )}
        </AnimatePresence>
      </button>
      <p className="text-center font-bold text-ink-2">{f(d.spy.spiesInGame, { n: spyCount })}</p>
      {onSeen && (
        <Btn game="spy" block size="lg" disabled={seen} onClick={onSeen} icon={seen ? <Check size={20} /> : <EyeOff size={20} />}>
          {d.spy.seen}
        </Btn>
      )}
    </div>
  );
}

function Roles({ client, view, v, tv }: { client: RoomClient; view: RoomView; v: SpyV; tv?: boolean }) {
  const { d, f } = useI18n();
  const g = useGameAct(client);
  const me = view.me?.id ?? "";
  const players = view.players.filter((p) => v.players.includes(p.id));
  return (
    <div className="mx-auto max-w-xl space-y-6">
      <h1 className="display text-center text-4xl">{d.spy.rolesTitle}</h1>
      {v.role && !tv ? <RoleCard role={v.role} word={v.word} pack={v.packName} emoji={v.packEmoji} spyCount={v.spyCount} seen={v.seen.includes(me)} onSeen={() => g({ t: "seen" })} /> : <AudienceNote />}
      <div className="space-y-2 text-center">
        <p className="font-bold text-ink-3">{f(d.spy.seenCount, { a: v.seen.length, b: v.players.length })}</p>
        <PlayerStrip players={players} done={v.seen} />
      </div>
      {view.me?.isHost && <Btn variant="soft" block onClick={() => g({ t: "startQuestions" })}>{d.spy.startQuestions}</Btn>}
    </div>
  );
}

/** Pass-and-play: the phone goes round the table, each player peeks alone. */
function LocalRoles({ client, view, v }: { client: RoomClient; view: RoomView; v: SpyV }) {
  const { d } = useI18n();
  const next = v.players.find((id) => !v.seen.includes(id)) ?? null;
  const [showing, setShowing] = useState(false);
  const p = pl(view, next);
  useEffect(() => setShowing(false), [next]);
  if (!p || !client.viewAs) return null;
  const their = client.viewAs(p.id).view as SpyV;
  return (
    <div className="mx-auto max-w-xl space-y-6 text-center">
      {!showing ? (
        <div className="space-y-6 py-6">
          <p className="text-xl font-bold text-ink-2">{d.local.passTo}</p>
          <Avatar index={p.avatar} color={p.color} size={130} mood="idle" />
          <p className="display text-5xl">{p.name}</p>
          <Btn game="spy" size="lg" block onClick={() => { setShowing(true); avatarVoice(p.avatar); }}>
            {d.spy.passReady.replace("{name}", p.name)}
          </Btn>
        </div>
      ) : (
        <RoleCard
          role={their.role}
          word={their.word}
          pack={their.packName}
          emoji={their.packEmoji}
          spyCount={their.spyCount}
          onSeen={() => {
            setShowing(false);
            void client.actAs!(p.id, { t: "g", a: { t: "seen", as: p.id } });
          }}
        />
      )}
      <PlayerStrip players={view.players.filter((x) => v.players.includes(x.id))} done={v.seen} size={40} />
    </div>
  );
}

function Question({ client, view, v, tv }: { client: RoomClient; view: RoomView; v: SpyV; tv?: boolean }) {
  const { d, f } = useI18n();
  const g = useGameAct(client);
  const [text, setText] = useState("");
  const me = view.me?.id ?? "";
  const voice = view.settings.mode === "voice" || client.local;
  const speaker = pl(view, v.speaker);
  const order = v.order.map((id) => pl(view, id)).filter(Boolean) as PlayerPublic[];
  useEffect(() => setText(""), [v.qi]);
  const lastSpeaker = useRef<string | null>(null);
  useEffect(() => {
    if (speaker && speaker.id !== lastSpeaker.current) {
      lastSpeaker.current = speaker.id;
      if (speaker.id === me) {
        play("ding");
        haptic([15, 40, 15]);
      }
    }
  }, [speaker, me]);
  const canNext = voice && (view.me?.isHost || v.speaker === me || client.local);
  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <p className="text-center font-extrabold text-ink-3">{f(d.spy.qTitle, { n: v.qi + 1, total: v.qTotal })}</p>
      <motion.div key={v.qi} initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="surface p-6 text-center sm:p-10">
        <p className="text-[clamp(26px,4.6vw,44px)] leading-tight font-extrabold">{v.question}</p>
      </motion.div>
      {v.deadline && <TimerBar deadline={v.deadline} total={v.phaseMs} offset={client.offset()} paused={view.paused} color="var(--color-spy)" />}
      {voice ? (
        <div className="space-y-5">
          {speaker && (
            <div className="flex flex-col items-center gap-2">
              <Avatar index={speaker.avatar} color={speaker.color} size={96} mood="talk" />
              <p className="display text-3xl">{speaker.id === me ? d.spy.yourTurn : f(d.spy.turnOf, { name: speaker.name })}</p>
            </div>
          )}
          <div className="text-center">
            <p className="mb-2 text-sm font-bold text-ink-3">{d.spy.order}</p>
            <PlayerStrip players={order} highlight={v.speaker} done={v.order.slice(0, v.speakerIndex)} size={42} />
          </div>
          {canNext && !tv && (
            <Btn game="spy" block size="lg" onClick={() => g({ t: "next" })} icon={<Check size={20} />}>
              {client.local ? d.common.next : d.spy.doneTurn}
            </Btn>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {v.players.includes(me) && !tv ? (
            v.myAnswer ? (
              <p className="surface-2 p-4 text-center text-xl font-extrabold">{v.myAnswer}</p>
            ) : (
              <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (text.trim()) void g({ t: "answer", text: text.trim() }); }}>
                <input className="field text-lg" maxLength={SPY_MAX_ANSWER} value={text} placeholder={d.spy.answerPh} onChange={(e) => setText(e.target.value)} />
                <Btn type="submit" game="spy" disabled={!text.trim()} icon={<Send size={20} />} aria-label={d.bluff.send} />
              </form>
            )
          ) : (
            <AudienceNote />
          )}
          <p className="text-center font-bold text-ink-3">{f(d.spy.answered, { a: v.answered.length, b: v.players.length })}</p>
          <PlayerStrip players={order} done={v.answered} />
        </div>
      )}
    </div>
  );
}

function Answers({ client, view, v }: { client: RoomClient; view: RoomView; v: SpyV }) {
  const { d } = useI18n();
  const g = useGameAct(client);
  const last = v.answers[v.answers.length - 1];
  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <h2 className="display text-center text-3xl">{d.spy.answersTitle}</h2>
      {last && <p className="text-center text-xl font-bold text-ink-2">{last.q}</p>}
      <ul className="grid gap-3 sm:grid-cols-2">
        {Object.entries(last?.a ?? {}).map(([id, a], i) => {
          const p = pl(view, id);
          return (
            <motion.li key={id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }} className="surface flex items-center gap-3 p-4">
              {p && <Avatar index={p.avatar} color={p.color} size={46} />}
              <span className="min-w-0">
                <span className="block text-sm font-bold text-ink-3">{p?.name}</span>
                <span className="block text-lg font-extrabold">{a}</span>
              </span>
            </motion.li>
          );
        })}
      </ul>
      <TimerBar deadline={v.deadline} total={v.phaseMs} offset={client.offset()} paused={view.paused} color="var(--color-spy)" tickLast={0} />
      {view.me?.isHost && <Btn game="spy" block onClick={() => g({ t: "next" })}>{d.common.next}</Btn>}
    </div>
  );
}

function Vote({ client, view, v, tv }: { client: RoomClient; view: RoomView; v: SpyV; tv?: boolean }) {
  const { d, f } = useI18n();
  const g = useGameAct(client);
  const me = view.me?.id ?? "";
  const candidates = view.players.filter((p) => v.players.includes(p.id));
  const canVote = v.players.includes(me) && !tv;
  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="text-center">
        <h2 className="display text-4xl">{d.spy.voteTitle}</h2>
        <p className="mt-2 text-ink-2">{d.spy.voteSub}</p>
      </div>
      {v.deadline && <TimerBar deadline={v.deadline} total={v.phaseMs} offset={client.offset()} paused={view.paused} color="var(--color-spy)" />}
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
        {candidates.map((p) => {
          const mine = v.myVote === p.id;
          const self = p.id === me;
          return (
            <button
              key={p.id}
              className="tile flex flex-col items-center gap-2 p-3"
              disabled={!canVote || self}
              style={{ ["--b" as string]: mine ? "var(--color-spy)" : "var(--color-ground-3)", color: mine ? "#132100" : undefined, opacity: self ? 0.5 : 1 }}
              onClick={() => {
                play("tap");
                void g({ t: "vote", target: p.id });
              }}
            >
              <Avatar index={p.avatar} color={p.color} size={64} mood={mine ? "sad" : "still"} />
              <span className="w-full truncate font-extrabold">{p.name}</span>
              {v.voted.includes(p.id) && <span className="text-xs font-bold opacity-70">✓</span>}
            </button>
          );
        })}
      </div>
      <p className="text-center font-bold text-ink-3">{f(d.spy.votesIn, { a: v.voted.length, b: v.players.length })}</p>
      {view.me?.isHost && <Btn variant="soft" block onClick={() => g({ t: "closeVote" })}>{d.spy.closeVote}</Btn>}
    </div>
  );
}

/** Pass-and-play vote: the table argues, then taps who they accuse. */
function LocalVote({ client, view, v }: { client: RoomClient; view: RoomView; v: SpyV }) {
  const { d } = useI18n();
  const g = useGameAct(client);
  const [picked, setPicked] = useState<string[]>([]);
  const candidates = view.players.filter((p) => v.players.includes(p.id));
  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="text-center">
        <h2 className="display text-4xl">{d.spy.groupAccuse}</h2>
        <p className="mt-2 text-ink-2">{d.spy.spiesInGame.replace("{n}", String(v.spyCount))}</p>
      </div>
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
        {candidates.map((p) => {
          const on = picked.includes(p.id);
          return (
            <button
              key={p.id}
              className="tile flex flex-col items-center gap-2 p-3"
              style={{ ["--b" as string]: on ? "var(--color-spy)" : "var(--color-ground-3)", color: on ? "#132100" : undefined }}
              onClick={() => {
                play("tap");
                setPicked((l) => (on ? l.filter((x) => x !== p.id) : [...l, p.id].slice(-v.spyCount)));
              }}
            >
              <Avatar index={p.avatar} color={p.color} size={64} />
              <span className="w-full truncate font-extrabold">{p.name}</span>
            </button>
          );
        })}
      </div>
      <Btn game="spy" block size="lg" disabled={picked.length === 0} onClick={() => g({ t: "accuse", targets: picked })}>
        {d.spy.accuse}
      </Btn>
    </div>
  );
}

function Reveal({ client, view, v }: { client: RoomClient; view: RoomView; v: SpyV }) {
  const { d, f } = useI18n();
  const g = useGameAct(client);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    play("drumroll", { intensity: 0.6 });
    const t = setTimeout(() => {
      setOpen(true);
      play(v.caught.length === v.spies.length && v.spies.length ? "fanfare" : "sting");
    }, 2100);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const wrong = v.accused.filter((id) => !v.spies.includes(id));
  return (
    <div className="mx-auto max-w-3xl space-y-6 text-center">
      <h2 className="display text-4xl">{v.spies.length > 1 ? d.spy.revealTitlePlural : d.spy.revealTitle}</h2>
      <div className="flex min-h-[220px] flex-wrap items-center justify-center gap-8">
        {open ? (
          v.spies.map((id, i) => {
            const p = pl(view, id);
            if (!p) return null;
            const caught = v.caught.includes(id);
            return (
              <motion.div key={id} initial={{ scale: 0, rotate: -20 }} animate={{ scale: 1, rotate: 0 }} transition={{ delay: i * 0.25, type: "spring", stiffness: 300, damping: 14 }} className="flex flex-col items-center gap-2">
                <Avatar index={p.avatar} color={p.color} size={120} mood={caught ? "sad" : "happy"} />
                <p className="display text-3xl">{p.name}</p>
                <p className="text-xl font-black" style={{ color: caught ? "var(--color-spy)" : "var(--color-candy)" }}>{caught ? d.spy.caught : d.spy.escaped}</p>
              </motion.div>
            );
          })
        ) : (
          <motion.div animate={{ rotate: [0, -6, 6, -4, 4, 0] }} transition={{ repeat: Infinity, duration: 0.8 }}>
            <Avatar index={15} size={130} disc={false} />
          </motion.div>
        )}
      </div>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="space-y-3">
          {v.accused.length === 0 && <p className="text-lg font-bold text-ink-2">{d.spy.noOneAccused}</p>}
          {wrong.map((id) => (
            <p key={id} className="text-lg font-bold text-ink-2">{f(d.spy.wrongAccused, { name: pl(view, id)?.name ?? "?" })}</p>
          ))}
          <p className="text-xl font-extrabold">{f(d.spy.wordWas, { word: v.word ?? "" })}</p>
          {v.votes && <VoteLines view={view} votes={v.votes} />}
        </motion.div>
      )}
      {view.me?.isHost && open && <Btn game="spy" size="lg" onClick={() => g({ t: "next" })}>{d.common.next}</Btn>}
    </div>
  );
}

function VoteLines({ view, votes }: { view: RoomView; votes: Record<string, string> }) {
  return (
    <ul className="mx-auto grid max-w-lg gap-2 text-left">
      {Object.entries(votes).map(([from, to]) => {
        const a = pl(view, from);
        const b = pl(view, to);
        if (!a || !b) return null;
        return (
          <li key={from} className="flex items-center gap-2 rounded-2xl bg-ground-3 px-3 py-2 font-bold">
            <Avatar index={a.avatar} color={a.color} size={30} /> <span className="truncate">{a.name}</span>
            <span className="text-ink-3">→</span>
            <Avatar index={b.avatar} color={b.color} size={30} /> <span className="truncate">{b.name}</span>
          </li>
        );
      })}
    </ul>
  );
}

function Guess({ client, view, v }: { client: RoomClient; view: RoomView; v: SpyV }) {
  const { d } = useI18n();
  const g = useGameAct(client);
  const me = view.me?.id ?? "";
  const iGuess = v.caught.includes(me) || client.local;
  const guesser = client.local ? v.caught.find((id) => !(v.guesses ?? {})[id]) ?? v.caught[0] : me;
  return (
    <div className="mx-auto max-w-2xl space-y-6 text-center">
      <h2 className="display text-4xl">{d.spy.guessTitle}</h2>
      {iGuess && v.guessOptions ? (
        <>
          {client.local && <p className="text-xl font-bold">{pl(view, guesser)?.name}</p>}
          <p className="text-ink-2">{d.spy.guessSub}</p>
          <div className="grid grid-cols-2 gap-3">
            {v.guessOptions.map((w) => (
              <button key={w} className="tile px-4 py-4 text-lg font-extrabold" onClick={() => { play("tap"); void g({ t: "guess", word: w, as: guesser }); }}>
                {w}
              </button>
            ))}
          </div>
        </>
      ) : (
        <p className="text-xl font-bold text-ink-2">{d.spy.guessWait}</p>
      )}
      {v.deadline && <TimerBar deadline={v.deadline} total={v.phaseMs} offset={client.offset()} paused={view.paused} color="var(--color-spy)" />}
    </div>
  );
}

function Result({ client, view, v }: { client: RoomClient; view: RoomView; v: SpyV }) {
  const { d, f } = useI18n();
  const act = useAct(client);
  const civWin = v.winner === "civ";
  const me = view.me?.id ?? "";
  const iSpy = v.spies.includes(me);
  useEffect(() => {
    const iWon = (iSpy && !civWin) || (!iSpy && civWin);
    play(iWon || client.local ? "win" : "fooled");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const winners = civWin ? v.players.filter((id) => !v.spies.includes(id)) : v.spies;
  return (
    <div className="mx-auto max-w-3xl space-y-8">
      <Banner title={civWin ? d.spy.civWin : d.spy.spyWin} sub={f(d.spy.wordWas, { word: v.word ?? "" })} color={civWin ? "var(--color-spy)" : "var(--color-candy)"} />
      {v.guesses &&
        Object.entries(v.guesses).map(([id, w]) => (
          <p key={id} className="text-center text-lg font-bold text-ink-2">
            {w === v.word ? f(d.spy.guessed, { name: pl(view, id)?.name ?? "?" }) : f(d.spy.guessedWrong, { name: pl(view, id)?.name ?? "?", word: w })}
          </p>
        ))}
      <div className="flex flex-wrap justify-center gap-4">
        {view.players.filter((p) => v.players.includes(p.id)).map((p) => (
          <div key={p.id} className="flex flex-col items-center gap-1">
            <Avatar index={p.avatar} color={p.color} size={72} mood={winners.includes(p.id) ? "happy" : "sad"} />
            <span className="max-w-[9ch] truncate font-bold">{p.name}</span>
            {v.spies.includes(p.id) && <span className="text-xs font-black text-candy">{d.avatars.spy}</span>}
          </div>
        ))}
      </div>
      <Scoreboard view={view} rows={v.players.map((id) => ({ id, score: v.scores[id] ?? 0, delta: v.roundPts[id] }))} title={d.room.scoreboard} />
      {view.me?.isHost ? (
        <div className="flex flex-col gap-3 sm:flex-row">
          <Btn game="spy" size="lg" block onClick={() => act({ t: "again" })}>{d.room.newRound}</Btn>
          <Btn variant="soft" size="lg" block onClick={() => act({ t: "lobby" })}>{d.room.backToLobby}</Btn>
        </div>
      ) : (
        <p className="text-center font-bold text-ink-2">{d.lobby.waitingHost}</p>
      )}
    </div>
  );
}
